import {PRODUCT_TYPES} from 'components/customer/Product/helpers/mapInitialValues';
import {cloneDeep} from 'lodash';
import {toFixed, validSubpanels} from 'shared/helpers';
import {Product} from 'components/customer/Product/entity/Product';
import {Shelf} from 'components/customer/Product/entity/Shelf';
import {CabinetDrawerType} from 'components/customer/Product/entity/Cabinet';

const DOOR_HANG_TYPE = {
    PAIR: 2,
};
interface ProductDataStore {
    current: Product;
}

type ShelfFields =
    | 'shelf_type'
    | 'shelf_style'
    | 'shelf_position'
    | 'shelf_offset'
    | 'upper_shelf_type'
    | 'upper_shelf_style'
    | 'upper_shelf_position'
    | 'upper_shelf_offset'
    | 'middle_shelf_type'
    | 'middle_shelf_style'
    | 'middle_shelf_position'
    | 'middle_shelf_offset'
    | 'lower_shelf_type'
    | 'lower_shelf_style'
    | 'lower_shelf_position'
    | 'lower_shelf_offset';

interface MapProps {
    values: Product;
    productDataStore: ProductDataStore;
    shelvesStructureStore: {
        current: {
            [key: string]: ShelfFields[];
        };
    };
    shelfTypes: {
        current: {
            [key: string]: number;
        };
    };
    shelfStyles: {
        current: {
            [key: string]: number;
        };
    };
}

export const mapProductData = ({
    values,
    productDataStore,
    shelvesStructureStore,
    shelfTypes,
    shelfStyles,
}: MapProps) => {
    values.cabinet_style = productDataStore.current.cabinet.attributes.style; // Set the type of Cabinet
    values.cabinet_name =
        productDataStore.current.cabinet.attributes.original_name; // Set the name of Cabinet

    if (
        productDataStore.current.cabinet_details &&
        productDataStore.current.cabinet_details.cabinet_tops
    ) {
        const top = productDataStore.current.cabinet_details.cabinet_tops.find(
            (top) => values.cabinet_top == top.id
        );

        values.cabinet_top_details = typeof top === 'undefined' ? 'None' : top;
    }
    if (
        productDataStore.current.doors &&
        productDataStore.current.doors.length
    ) {
        const doors = cloneDeep(productDataStore.current.doors);

        values.doors = doors; // set door values from formik values

        values.doors = values.doors.map((door) => {
            door.door_hang_type = values.door_hang_type;
            door.horizontal_positions = values.hori_mid_rail_positions;
            door.vertical_positions = values.vert_mid_rail_positions;
            return door;
        });

        if (values.glassSubPanels) {
            let subPanelCount = 1;
            const updatedSubPanels = validSubpanels(
                values.glassSubPanels
            ) as boolean[][];

            updatedSubPanels.forEach((subPanels) => {
                subPanels.forEach((subPanel) => {
                    switch (subPanelCount) {
                        case 1:
                            values.sub_panel_1 = subPanel;
                            break;
                        case 2:
                            values.sub_panel_2 = subPanel;
                            break;
                        case 3:
                            values.sub_panel_3 = subPanel;
                            break;
                        case 4:
                            values.sub_panel_4 = subPanel;
                            break;
                        case 5:
                            values.sub_panel_5 = subPanel;
                            break;
                        case 6:
                            values.sub_panel_6 = subPanel;
                            break;
                        case 7:
                            values.sub_panel_7 = subPanel;
                            break;
                        case 8:
                            values.sub_panel_8 = subPanel;
                            break;
                        case 9:
                            values.sub_panel_9 = subPanel;
                            break;
                    }

                    subPanelCount++;
                });
            });
        }
    }

    if (values.hasOwnProperty('rail_hori') && values.rail_hori == 0)
        values.rail_hori = [];

    if (values.hasOwnProperty('rail_vert') && values.rail_vert == 0)
        values.rail_vert = [];

    // door_hang_type check this and cabinetStyle = 5 and set hinge amount equal to drilling. incase of pair hinge amount * 2.
    if (
        productDataStore.current.hasOwnProperty('cabinet') &&
        productDataStore.current.cabinet.hasOwnProperty('attributes') &&
        productDataStore.current.cabinet.attributes.style ===
            PRODUCT_TYPES.COMPONENTS &&
        values.hasOwnProperty('drillings') &&
        values.hasOwnProperty('door_hang_type')
    ) {
        values.hinge = values.drillings.length;

        if (values.door_hang_type === DOOR_HANG_TYPE.PAIR) {
            values.hinge = 2 * values.drillings.length;
        }
    }

    // When a product has drawer_runner_specs and drawer_type as scalar fields
    // (e.g., a Drawer Component product type 355), convert them to arrays.
    // Note: drawer_face_height is overwritten to [-1] here as a sentinel, but
    // is then restored from drawers[].drawer_face_height below if drawers exists.
    // This relies on normalizeDrawerValues syncing the flat drawer_face_height
    // array into drawers[].drawer_face_height before this function runs.
    if (
        values.hasOwnProperty('drawer_runner_specs') &&
        values.hasOwnProperty('drawer_type')
    ) {
        values.drawer_runner_specs = [values.drawer_runner_specs as number];
        values.drawer_type = [values.drawer_type as number];
        values.drawer_face_height = [-1];
        values.drawer_style = [-1];
    }

    // Extracts per-drawer values from the drawers array into flat arrays.
    // This overwrites drawer_face_height with values from drawers[].drawer_face_height,
    // so it's critical that normalizeDrawerValues has synced the flat array into
    // drawers before reaching this point.
    if (values.hasOwnProperty('drawers')) {
        let drawerType = CabinetDrawerType.fixed;
        const productDrawerType =
            productDataStore?.current?.cabinet?.attributes?.drawer_type;

        if (
            typeof productDataStore.current.cabinet.attributes.drawer_type ==
            'string'
        ) {
            drawerType =
                CabinetDrawerType[
                    productDrawerType as keyof typeof CabinetDrawerType
                ];
        }

        if (drawerType == CabinetDrawerType.inner_custom) {
            values.drawers = values.drawers.map((drawer) => ({
                ...drawer,
                drawer_style: drawerType,
            }));

            values.drawers = values.drawers.slice(0, values.drawer_amount);
        } else {
            if (values.drawer_amount === undefined) {
                values.drawer_amount = values.drawers.length;
            }
        }

        values.drawer_face_height = values.drawers.map(
            (drawer) => drawer.drawer_face_height
        );
        values.drawer_runner_specs = values.drawers.map(
            (drawer) => drawer.drawer_runner_specs
        );
        values.drawer_style = values.drawers.map(
            (drawer) => drawer.drawer_style
        );
        values.drawer_type = values.drawers.map((drawer) =>
            drawer.hasOwnProperty('insert_id')
                ? drawer.insert_id
                : drawer.drawer_type
        );

        if (
            values.drawers.length > 0 &&
            values.drawers[0].hasOwnProperty('position_to_bottom')
        ) {
            values.drawer_position_to_bottom = values.drawers.map(
                (drawer) => drawer.position_to_bottom
            );
        }
    }

    if (values.hasOwnProperty('drawer_face_type')) {
        if (Number(values.job_cabinet_door_advanced) !== 1) {
            values.drawer_face_type = 2;
        }
    }

    if (
        values.hasOwnProperty('cabinet_upper_partition_height') &&
        Number(values.cabinet_upper_partition_height) <= 1
    ) {
        values.cabinet_upper_partition_height = `${
            Number(values.cabinet_upper_partition_height) * 100
        }%`;
    }

    if (
        values.hasOwnProperty('cabinet_lower_partition_height') &&
        Number(values.cabinet_lower_partition_height) <= 1
    ) {
        values.cabinet_lower_partition_height = `${
            Number(values.cabinet_lower_partition_height) * 100
        }%`;
    }

    if (values.hasOwnProperty('cabinet_edge_L1')) {
        values.cabinet_edge_l1 = values.cabinet_edge_L1;
    }

    if (values.hasOwnProperty('cabinet_edge_L2')) {
        values.cabinet_edge_l2 = values.cabinet_edge_L2;
    }

    if (values.hasOwnProperty('cabinet_edge_W1')) {
        values.cabinet_edge_w1 = values.cabinet_edge_W1;
    }

    if (values.hasOwnProperty('cabinet_edge_W2')) {
        values.cabinet_edge_w2 = values.cabinet_edge_W2;
    }

    // shelves
    Object.keys(shelvesStructureStore.current).forEach((fieldset) => {
        const shelfStructure = shelvesStructureStore.current[String(fieldset)];
        let shelves =
            values.hasOwnProperty(fieldset) &&
            (values[fieldset as ShelfFields] as Shelf[]);
        let shelfStyle: number;

        if (fieldset === 'shelves') {
            if (
                values.hasOwnProperty('cabinet_simple_shelves') &&
                values.cabinet_simple_shelves
            ) {
                shelfStyle =
                    values.hasOwnProperty('simple_shelf_type') &&
                    Number(values.simple_shelf_type);

                // 3D backup/history payloads can have cabinet_simple_shelves
                // without a shelves array (e.g. pull-out / tall / base updates).
                if (
                    !Array.isArray(shelves) ||
                    values.simple_shelves_amount != shelves.length
                ) {
                    shelves = [];
                    values.shelves = [];
                } else if (shelves.length) {
                    const advancedShelves = shelves;
                    shelves = advancedShelves.map((shelf, i) => ({
                        ...shelf,
                        shelf_position: toFixed(
                            (i + 1) / (advancedShelves.length + 1),
                            4
                        ),
                    }));
                    values.shelves = shelves;
                }
            }
        }

        if (shelves && Array.isArray(shelves) && shelves.length) {
            /* eslint-disable security/detect-object-injection -- none of the keys are user entered values */
            shelfStructure.forEach((field) => {
                values[field] = [];

                for (let i = 0; i < shelves.length; i++) {
                    if (field.indexOf('position') > -1) {
                        values[field].push(
                            Number(shelves[i][field as keyof Shelf])
                        );
                    } else if (
                        field.indexOf('shelf_style') > -1 &&
                        typeof shelfStyle === 'number'
                    ) {
                        values[field].push(shelfStyle);
                    } else {
                        values[field].push(
                            Number(shelves[i][field as keyof Shelf])
                        );
                    }
                }
            });
            /* eslint-enable security/detect-object-injection -- none of the keys are user entered values */
        } else {
            let shelvesCount = 0;
            const shelfType =
                shelfTypes.current.hasOwnProperty(fieldset) &&
                shelfTypes.current[String(fieldset)];
            let shelfStyle =
                shelfStyles.current.hasOwnProperty(fieldset) &&
                shelfStyles.current[String(fieldset)];

            switch (fieldset) {
                case 'shelves':
                    shelvesCount = values.hasOwnProperty(
                        'simple_shelves_amount'
                    )
                        ? values.simple_shelves_amount
                        : 0;
                    shelfStyle =
                        values.hasOwnProperty('simple_shelf_type') &&
                        values.simple_shelf_type;

                    if (typeof shelfStyle == 'boolean' && !shelfStyle) {
                        shelfStyle =
                            productDataStore.current.cabinet.attributes.hasOwnProperty(
                                'shelf_type'
                            ) &&
                            productDataStore.current.cabinet.attributes
                                .shelf_type;
                    }
                    break;
            }

            /* eslint-disable security/detect-object-injection -- none of the keys are user entered values */
            shelfStructure.forEach((field) => {
                values[field] = [];

                for (let i = 0; i < shelvesCount; i++) {
                    if (field.indexOf('type') >= 0) {
                        values[field].push(shelfType);
                    }

                    if (field.indexOf('style') >= 0) {
                        values[field].push(shelfStyle);
                    }

                    if (field.indexOf('position') >= 0) {
                        values[field].push(
                            toFixed((i + 1) / (shelvesCount + 1), 4)
                        );
                    }

                    if (field.indexOf('offset') >= 0) {
                        values[field].push(
                            values.shelf_set_back
                                ? Number(values.shelf_set_back)
                                : 5
                        );
                    }
                }
            });
            /* eslint-enable security/detect-object-injection -- none of the keys are user entered values */
        }
    });
    // Shelves

    // drillings
    if (values.hasOwnProperty('drillings')) {
        if (
            productDataStore.current.hasOwnProperty('cabinet') &&
            productDataStore.current.cabinet.hasOwnProperty('attributes') &&
            productDataStore.current.cabinet.attributes.hasOwnProperty(
                'product_type'
            ) &&
            productDataStore.current.cabinet.attributes.product_type == 5
        ) {
            values.drilling_offset_x = values.drillings.map(
                (drilling) => drilling.drilling_offset_x
            );
            values.drilling_offset_y = values.drillings.map(
                (drilling) => drilling.drilling_offset_y
            );
            values.drilling_num_holes = values.drillings.map(
                (drilling) => drilling.drilling_num_holes
            );
            values.drilling_pitch = values.drillings.map(
                (drilling) => drilling.drilling_pitch
            );
        } else {
            values.hinge = values.drillings.map((drilling) => {
                return {
                    offset_x: drilling['drilling_offset_x'],
                    offset_y: drilling['drilling_offset_y'],
                    num_holes: drilling['drilling_num_holes'],
                    pitch: drilling['drilling_pitch'],
                };
            });
        }
    }

    // drillings

    if (values.hasOwnProperty('cabinet_left_depth')) {
        values.cabinet_width_depth = values.cabinet_left_depth;
    }

    if (values.hasOwnProperty('cabinet_right_depth')) {
        values.cabinet_length_depth = values.cabinet_right_depth;
    }

    return values;
};
